import {
  buildSingleHedgeParams,
  validateSingleHedge,
} from '@/features/hedge/lib/build-single-hedge-params'
import { defaultHedgeForm } from '@/features/hedge/lib/hedge-bands'
import {
  distinctPositionUnderlyings,
  hedgeInstrumentScope,
  type HedgeInstrumentScope,
  resolvePositionUnderlying,
} from '@/features/hedge/lib/hedge-instrument'
import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import type { HedgeFormModel, HedgeValidationError } from '@/features/hedge/types'
import { positionNotionalUsd } from '@/features/home/lib/home-exposure'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import type { SingleHedgeTabState } from '@/state/order'
import type { SingleHedgeAlgoParams } from '@/types/generated/algo'
import type { Position } from '@/types/generated/common/trading'
import type { Exchange, Instrument } from '@/types/generated/common/types'

export interface SingleHedgeTabContext {
  // The ticket's first row: its underlying is the default scope, and it is the hedge
  // instrument itself when it is a perp on the scoped underlying.
  selectedSymbol: string | undefined
  // Positions of the hedging account only.
  positions: Position[]
  // The hedging account's venue.
  instrumentMap: Map<string, Instrument>
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
}

/** Positions the hedge account can see — the same filter the hedge dialog applies. */
export function positionsForAccount(positions: Position[], accountId: string): Position[] {
  return positions.filter((p) => !p.accountId || p.accountId === accountId)
}

/**
 * The context for a hedge by `accountId` on `exchange`: that account's positions and that
 * venue's instruments, falling back to the ticket's own map when the venue's isn't loaded.
 */
export function singleHedgeContext(args: {
  selectedSymbol: string | undefined
  accountId: string
  exchange: Exchange
  allPositions: Position[]
  instrumentMap: Map<string, Instrument>
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
}): SingleHedgeTabContext {
  return {
    selectedSymbol: args.selectedSymbol,
    positions: positionsForAccount(args.allPositions, args.accountId),
    instrumentMap: args.multiExchangeInstrumentMap.get(args.exchange) ?? args.instrumentMap,
    multiExchangeInstrumentMap: args.multiExchangeInstrumentMap,
  }
}

/** The selected instrument's underlying, or '' with nothing selected. */
export function selectedUnderlying(ctx: SingleHedgeTabContext): string {
  return ctx.selectedSymbol ? resolveBaseCurrency(ctx.instrumentMap, ctx.selectedSymbol) : ''
}

/**
 * The underlying the scope follows until one is picked: the selected instrument's when the
 * account holds it, else the account's largest held underlying by notional, else none. A row
 * seeded from a focused chart tab says nothing about what this account can hedge.
 */
export function autoUnderlying(ctx: SingleHedgeTabContext): string {
  const notional = new Map<string, number>()
  for (const p of ctx.positions) {
    const underlying = resolvePositionUnderlying(
      p,
      ctx.instrumentMap,
      ctx.multiExchangeInstrumentMap
    )
    if (underlying)
      notional.set(underlying, (notional.get(underlying) ?? 0) + positionNotionalUsd(p))
  }
  const selected = selectedUnderlying(ctx)
  if (selected && notional.has(selected)) return selected
  let largest = ''
  for (const [underlying, usd] of notional) {
    if (!largest || usd > notional.get(largest)!) largest = underlying
  }
  return largest
}

/** The underlying scope's asset: the picked one, else the auto rule above. */
export function scopedUnderlying(state: SingleHedgeTabState, ctx: SingleHedgeTabContext): string {
  return state.underlying ?? autoUnderlying(ctx)
}

/**
 * The underlyings the picker offers: those the account holds (the hedge dialog's own list),
 * and the selected instrument's apart from them when the account holds none of it.
 */
export function singleHedgeUnderlyingOptions(ctx: SingleHedgeTabContext): {
  held: string[]
  other: string[]
} {
  const held = distinctPositionUnderlyings(
    ctx.positions,
    ctx.instrumentMap,
    ctx.multiExchangeInstrumentMap
  ).sort()
  const selected = selectedUnderlying(ctx)
  return { held, other: selected && !held.includes(selected) ? [selected] : [] }
}

function selectedPerpFor(scope: HedgeInstrumentScope, ctx: SingleHedgeTabContext): string {
  const symbol = ctx.selectedSymbol
  if (!symbol || !scope.allowsExplicit || !scope.underlying) return ''
  const instrument = ctx.instrumentMap.get(symbol)
  if (!instrument || !isPerpInstrument(instrument)) return ''
  return resolveBaseCurrency(ctx.instrumentMap, symbol, instrument) === scope.underlying
    ? symbol
    : ''
}

function overrideFor(
  instrument: string,
  scope: HedgeInstrumentScope,
  ctx: SingleHedgeTabContext
): string {
  if (!instrument || !scope.allowsExplicit) return ''
  if (scope.underlying && resolveBaseCurrency(ctx.instrumentMap, instrument) !== scope.underlying) {
    return ''
  }
  return instrument
}

/**
 * The tab's scope and target as a hedge form model, so the hedge dialog's scope validation
 * and instrument resolution apply unchanged. A picked instrument the scope no longer permits is dropped, the way
 * the dialog drops it on a scope change.
 */
export function singleHedgeTabForm(
  state: SingleHedgeTabState,
  ctx: SingleHedgeTabContext
): HedgeFormModel {
  const defaults = defaultHedgeForm()
  const form: HedgeFormModel = {
    ...defaults,
    scopeMode: state.scopeMode,
    underlyingAsset: state.scopeMode === 'underlying' ? scopedUnderlying(state, ctx) : '',
    positionSymbols: state.scopeMode === 'positionGroup' ? state.positionSymbols : [],
    denomination: state.denomination,
    acceptable: { ...defaults.acceptable, target: state.target },
  }
  const scope = hedgeInstrumentScope(
    form,
    ctx.positions,
    ctx.instrumentMap,
    ctx.multiExchangeInstrumentMap
  )
  form.hedgeInstrument =
    state.hedgeInstrument === null
      ? selectedPerpFor(scope, ctx)
      : overrideFor(state.hedgeInstrument, scope, ctx)
  return form
}

export type SingleHedgeTabBuild =
  | { ok: true; params: SingleHedgeAlgoParams }
  | { ok: false; errors: HedgeValidationError[] }

export function buildSingleHedgeTabParams(
  state: SingleHedgeTabState,
  ctx: SingleHedgeTabContext,
  accountId: string
): SingleHedgeTabBuild {
  const form = singleHedgeTabForm(state, ctx)
  const errors = validateSingleHedge(
    form,
    state,
    ctx.positions,
    ctx.instrumentMap,
    ctx.multiExchangeInstrumentMap
  )
  if (errors.length > 0) return { ok: false, errors }
  return {
    ok: true,
    params: buildSingleHedgeParams(
      form,
      state,
      accountId,
      ctx.positions,
      ctx.instrumentMap,
      ctx.multiExchangeInstrumentMap
    ),
  }
}
